import React, { useEffect, useState } from 'react';
import { authApiClient } from '../../services/authApi';
import { cronBFamilyLabel } from './CronASummaryTable';
import { fmtTime, type CronBStage } from './CronBStageTable';

export type CronBPeriod = 'daily' | 'weekly';

export interface CronBPeriodRow {
  period_start: string;
  runs: number;
  last_status: string | null;
  counts: Record<string, number>;
  // export
  signal_rows?: number | null;
  // build
  built?: number;
  better?: number;
  same?: number;
  worse?: number;
  // build + merge
  held?: number;
  failed?: number;
  // merge
  merged?: number;
  held_by_reason?: Record<string, number>;
  backlog?: number | null;
  // revert
  reverted?: number;
  refused?: number;
}

export interface CronBSummaryRoute {
  route: string;
  region: string | null;
  last_run_at: string | null;
  scheduled: boolean;
  periods: CronBPeriodRow[];
}

const RANGE_OPTIONS: Record<CronBPeriod, { value: number; label: string }[]> = {
  daily: [
    { value: 7, label: 'Last 7 days' },
    { value: 14, label: 'Last 14 days' },
    { value: 30, label: 'Last 30 days' },
  ],
  weekly: [
    { value: 4, label: 'Last 4 weeks' },
    { value: 8, label: 'Last 8 weeks' },
    { value: 12, label: 'Last 12 weeks' },
  ],
};
const DEFAULT_RANGE: Record<CronBPeriod, number> = { daily: 14, weekly: 8 };

export const RETRIED_TOOLTIP =
  'Highest single run in the period: these docs stay pending and are retried every run, ' +
  'so runs are not added up.';

interface SummaryColumn {
  header: string;
  key: keyof CronBPeriodRow;
  tone?: string;
  title?: string;
}

const STAGE_COLUMNS: Record<CronBStage, SummaryColumn[]> = {
  export: [
    { header: 'Signal rows', key: 'signal_rows', title: 'Snapshot size at the last export of the period' },
  ],
  build: [
    { header: 'Built', key: 'built' },
    { header: 'Better', key: 'better', tone: 'text-green-700' },
    { header: 'Same', key: 'same' },
    { header: 'Worse', key: 'worse', tone: 'text-amber-700' },
    { header: 'Other holds', key: 'held', tone: 'text-amber-700' },
    { header: 'Errors', key: 'failed', tone: 'text-red-700', title: RETRIED_TOOLTIP },
  ],
  merge: [
    { header: 'Merged', key: 'merged', tone: 'text-green-700' },
    { header: 'Held', key: 'held', tone: 'text-amber-700' },
    { header: 'Errors', key: 'failed', tone: 'text-red-700', title: RETRIED_TOOLTIP },
    { header: 'Backlog', key: 'backlog', title: 'Better candidates still waiting, at the last merge of the period' },
  ],
  revert: [
    { header: 'Reverted', key: 'reverted' },
    { header: 'Refused', key: 'refused', tone: 'text-amber-700' },
  ],
};

const fmt = (v: unknown) => (typeof v === 'number' ? v.toLocaleString() : '—');

const reasonsTitle = (p: CronBPeriodRow) => {
  const held = Object.entries(p.held_by_reason || {});
  return held.length ? held.map(([k, v]) => `${k}: ${v}`).join('\n') : undefined;
};

/** Daily / Weekly rollup for one Cron B stage (GET /admin/cron-summary?cron=B). */
export const CronBSummaryTable: React.FC<{ stage: CronBStage; period: CronBPeriod }> = ({ stage, period }) => {
  const [range, setRange] = useState<number>(DEFAULT_RANGE[period]);
  const [routes, setRoutes] = useState<CronBSummaryRoute[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const days = period === 'weekly' ? range * 7 : range;
    setLoading(true);
    setError(null);
    authApiClient
      .get(`/admin/cron-summary?cron=B&stage=${stage}&period=${period}&days=${days}`)
      .then(res => { if (!cancelled) setRoutes(res.data.routes || []); })
      .catch(e => {
        if (!cancelled) setError(e?.response?.data?.detail || 'Failed to load Cron B summary');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [stage, period, range]);

  const columns = STAGE_COLUMNS[stage];
  const span = 3 + columns.length;
  const periodLabel = (start: string) => (period === 'weekly' ? `Week of ${start}` : start);

  return (
    <div>
      <div className="flex items-center justify-end mb-2">
        <label className="text-xs text-slate-500 mr-2" htmlFor={`cron-b-range-${stage}-${period}`}>Range</label>
        <select
          id={`cron-b-range-${stage}-${period}`}
          value={range}
          onChange={e => setRange(Number(e.target.value))}
          className="text-sm border border-slate-200 rounded px-2 py-1 bg-white"
        >
          {RANGE_OPTIONS[period].map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>
      <div className="bg-white rounded-lg border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left px-4 py-3 font-medium">Route</th>
              <th className="text-left px-4 py-3 font-medium">Region</th>
              <th className="text-left px-4 py-3 font-medium">{period === 'weekly' ? 'Week' : 'Day'}</th>
              {columns.map(c => (
                <th key={c.header} title={c.title} className="text-right px-4 py-3 font-medium">{c.header}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && (
              <tr><td colSpan={span} className="px-4 py-8 text-center text-slate-400">Loading…</td></tr>
            )}
            {!loading && error && (
              <tr><td colSpan={span} className="px-4 py-8 text-center text-red-600">{error}</td></tr>
            )}
            {!loading && !error && routes.map(r => {
              const badge = !r.scheduled && (
                <span
                  className="ml-2 px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-500"
                  title={`No ${stage} run in the last 7 days (last run: ${fmtTime(r.last_run_at)})`}
                >
                  Not scheduled
                </span>
              );
              if (r.periods.length === 0) {
                return (
                  <tr key={r.route} data-testid={`cron-b-sum-${r.route}`} className="text-slate-400">
                    <td className="px-4 py-3 text-slate-700" title={r.route}>{cronBFamilyLabel(r.route)}{badge}</td>
                    <td className="px-4 py-3">{r.region ?? '—'}</td>
                    <td className="px-4 py-3">{r.scheduled ? 'No runs in range' : '—'}</td>
                    {columns.map(c => <td key={c.header} className="px-4 py-3 text-right font-mono">—</td>)}
                  </tr>
                );
              }
              return r.periods.map((p, i) => (
                <tr key={`${r.route}|${p.period_start}`} data-testid={`cron-b-sum-${r.route}`}>
                  <td className="px-4 py-3 text-slate-700" title={i === 0 ? r.route : undefined}>
                    {i === 0 ? <>{cronBFamilyLabel(r.route)}{badge}</> : ''}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{i === 0 ? (r.region ?? '—') : ''}</td>
                  <td className="px-4 py-3 text-slate-700" title={`${p.runs} run(s), last: ${p.last_status ?? '—'}`}>
                    {periodLabel(p.period_start)}
                  </td>
                  {columns.map(c => (
                    <td key={c.header}
                        className={`px-4 py-3 text-right font-mono ${c.tone ?? ''}`}
                        title={c.key === 'held' && stage === 'merge' ? reasonsTitle(p) : c.title}>
                      {fmt(p[c.key])}
                    </td>
                  ))}
                </tr>
              ));
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
